import { motion } from "framer-motion";

const CHAPTERS = [
  {
    num: "01",
    title: "Solar Science",
    body: "We compute sun elevation and azimuth against your exact micro-geography, refreshed continuously. Every angle is measured, never guessed.",
  },
  {
    num: "02",
    title: "Zero Tracking",
    body: "No accounts, no cookies, no identity graphs. A comfort query leaves our system the moment it is answered. Nothing follows you home.",
  },
  {
    num: "03",
    title: "Micro-Climate Mapping",
    body: "Every building edge, tree line and canopy is modelled as a shadow-casting structure, so shade is a coordinate — not a coincidence.",
  },
  {
    num: "04",
    title: "Thermal Comfort",
    body: "Shade is not binary. We grade every zone by surface temperature, duration of cover and solar exposure, down to the square metre.",
  },
];

export default function Manifesto() {
  return (
    <section
      id="mission"
      data-testid="manifesto-section"
      className="border-b border-[var(--sp-line)]"
    >
      <div className="mx-auto max-w-[1500px] px-5 py-20 sm:px-8 lg:py-28">
        <div className="lg:grid lg:grid-cols-[1.3fr_0.85fr] lg:items-center lg:gap-16">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="mb-4 flex items-center gap-3 font-mono2 text-xs uppercase tracking-[0.28em] text-[var(--sun)]"
            >
              <span className="h-px w-10 bg-[var(--sun)]" />
              The Manifesto // 01
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-3xl font-display text-2xl font-extrabold uppercase leading-none tracking-tight sm:text-4xl lg:text-5xl"
            >
              Comfort is not luck. It is a calculation.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-6 max-w-md text-sm leading-relaxed text-[var(--sp-muted)]"
            >
              Built sunglasses-ready — the same high-contrast instinct that drives this page lives
              in the app's Sunlight Mode, tuned for reading in blazing midday glare.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-10 flex justify-center lg:mt-0"
          >
            <span className="app-screen w-full max-w-[260px]">
              <img
                src="/app-shots/08-sunlight-mode.jpeg"
                alt="ShadyParking Sunlight Mode: high-contrast amber-on-dark home screen for bright glare"
                loading="lazy"
                data-testid="manifesto-app-screen"
                className="aspect-[4/5] w-full object-contain"
              />
            </span>
          </motion.div>
        </div>

        <div className="mt-14 grid grid-cols-1 border-t border-l border-[var(--sp-line)] md:grid-cols-2">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.num}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`manifesto-chapter-${c.num}`}
              className="group relative border-b border-r border-[var(--sp-line)] p-8 transition-colors duration-500 hover:bg-[var(--sp-surface)] lg:p-12"
            >
              <span className="font-display text-6xl font-black leading-none text-[var(--sp-line)] transition-colors duration-500 group-hover:text-[var(--sun)] lg:text-7xl">
                {c.num}
              </span>
              <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-tight sm:text-2xl lg:text-3xl">
                {c.title}
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-[var(--sp-muted)] sm:text-base">
                {c.body}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
